import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import { useTranslation } from "react-i18next";
import {
  createCourierDhlShipment,
  cancelCourierDhlShipment,
  downloadCourierDhlLabel,
  fetchCourierDhlTracking,
} from "../../api/CourierControl/courierControlApi";

const FONT = 'Manrope, "Helvetica Neue", Arial, sans-serif';
const INK = "#252525";
const MUTED = "#656159";
const HAIRLINE = "#dad8d4";
const SURFACE = "#fbfaf8";
const ACCENT = "#4a6573";
const DHL_YELLOW = "#FFCC00";
const DHL_RED = "#D40511";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 204, 0, 0.55); }
  70% { box-shadow: 0 0 0 8px rgba(255, 204, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 204, 0, 0); }
`;

const slideAway = keyframes`
  0%, 18% { opacity: 1; transform: translateX(0) scale(1); }
  70% { opacity: 0.2; transform: translateX(46px) scale(0.82); }
  100% { opacity: 0.15; transform: translateX(52px) scale(0.78); }
`;

const shakeMark = keyframes`
  0%, 100% { transform: rotate(0deg); }
  30% { transform: rotate(-8deg); }
  60% { transform: rotate(6deg); }
`;

const glide = keyframes`
  0%, 12% { opacity: 1; transform: translateX(0) scale(1); }
  70% { opacity: 1; transform: translateX(52px) scale(0.92); }
  100% { opacity: 1; transform: translateX(52px) scale(0.92); }
`;

const dialogMotion = {
  backdrop: {
    sx: {
      backdropFilter: "blur(10px)",
      backgroundColor: "rgba(15, 23, 42, 0.45)",
    },
  },
};

const EVENT_ICONS = {
  delivered: CheckCircleOutlineIcon,
  courier: LocalShippingOutlinedIcon,
  customs: GavelOutlinedIcon,
  departed: FlightTakeoffOutlinedIcon,
  arrived: WarehouseOutlinedIcon,
  pickup: Inventory2OutlinedIcon,
  processed: WarehouseOutlinedIcon,
  other: PlaceOutlinedIcon,
};

async function errorText(err, fallback) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      return json.error || json.detail || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error || data?.detail || err?.message || fallback;
}

function sortEvents(events) {
  return [...events].sort((a, b) => {
    const ka = `${a.date || ""} ${a.time || ""}`;
    const kb = `${b.date || ""} ${b.time || ""}`;
    return kb.localeCompare(ka);
  });
}

function eventKind(event) {
  const text = `${event?.description || ""} ${event?.code || ""}`.toLowerCase();
  if (/deliver/.test(text)) return "delivered";
  if (/out with courier|out for delivery|with courier/.test(text))
    return "courier";
  if (/customs|clearance/.test(text)) return "customs";
  if (/depart|left /.test(text)) return "departed";
  if (/arriv/.test(text)) return "arrived";
  if (/pick/.test(text)) return "pickup";
  if (/process/.test(text)) return "processed";
  return "other";
}

function formatWhen(event) {
  return [event?.date, event?.time].filter(Boolean).join("  ·  ");
}

export default function CourierDhlDialog({
  row,
  onClose,
  onAwb,
  onDelivered,
  onCancelled,
  canDownloadLabel = false,
  canCancel = false,
  canCreate = false,
}) {
  const { t } = useTranslation();
  const open = Boolean(row);
  const id = row?.IdCourierControl;
  const [awb, setAwb] = useState("");
  const [tracking, setTracking] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const onDeliveredRef = useRef(onDelivered);
  onDeliveredRef.current = onDelivered;

  useEffect(() => {
    setAwb(String(row?.NumeroAwb || "").trim());
    setTracking(null);
    setError("");
    setConfirmOpen(false);
    setCancelOpen(false);
    setCancelReason("");
    setBusy("");
  }, [row]);

  useEffect(() => {
    if (!open || !id || !awb) return undefined;
    let cancelled = false;
    setBusy("track");
    setError("");
    fetchCourierDhlTracking(id)
      .then((data) => {
        if (cancelled) return;
        setTracking(data);
        if (data?.markedDelivered || data?.courierEstado === "ENTREGADO") {
          onDeliveredRef.current?.(id, {
            fechaEntrega: data.fechaEntrega || null,
          });
        }
      })
      .catch(async (err) => {
        if (!cancelled)
          setError(await errorText(err, t("courierControl.dhlTrackError")));
      })
      .finally(() => {
        if (!cancelled) setBusy("");
      });
    return () => {
      cancelled = true;
    };
  }, [open, id, awb, t]);

  const handleCreate = async () => {
    if (!id || !canCreate) return;
    setBusy("create");
    setError("");
    try {
      const data = await createCourierDhlShipment(id);
      const next = String(data?.awb || "").trim();
      setAwb(next);
      setConfirmOpen(false);
      if (next) {
        onAwb?.(id, next, {
          estado: data?.estado || null,
          fechaEnvio: data?.fechaEnvio || null,
        });
      }
    } catch (err) {
      setError(await errorText(err, t("courierControl.dhlCreateError")));
    } finally {
      setBusy("");
    }
  };

  const handleLabel = async () => {
    if (!id || !canDownloadLabel) return;
    setBusy("label");
    setError("");
    try {
      await downloadCourierDhlLabel(id);
    } catch (err) {
      setError(await errorText(err, t("courierControl.dhlLabelError")));
    } finally {
      setBusy("");
    }
  };

  const handleCancelShipment = async () => {
    if (!id || !canCancel) return;
    setBusy("cancel");
    setError("");
    try {
      const data = await cancelCourierDhlShipment(id, {
        reason:
          cancelReason.trim() || t("courierControl.dhlCancelReasonDefault"),
      });
      setCancelOpen(false);
      onCancelled?.(id, { estado: data?.estado || "CANCELADO" });
    } catch (err) {
      setCancelOpen(false);
      setError(await errorText(err, t("courierControl.dhlCancelError")));
    } finally {
      setBusy("");
    }
  };

  const events = sortEvents(
    Array.isArray(tracking?.events) ? tracking.events : [],
  );
  const latest = events[0];
  const latestKind = latest ? eventKind(latest) : null;
  const LatestIcon = latestKind ? EVENT_ICONS[latestKind] : null;
  const apiStatus = String(tracking?.status || "");
  const showApiStatus = apiStatus && !/^(success|failure)$/i.test(apiStatus);
  const estadoUpper = String(row?.Estado || "").toUpperCase();
  const canAskCancel =
    canCancel &&
    Boolean(awb) &&
    estadoUpper !== "ENTREGADO" &&
    estadoUpper !== "CANCELADO";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        transitionDuration={180}
        slotProps={dialogMotion}
        PaperProps={{
          sx: {
            borderRadius: "21px",
            bgcolor: SURFACE,
            color: INK,
            fontFamily: FONT,
            border: `1px solid ${HAIRLINE}`,
            boxShadow: "0 24px 60px rgba(37, 37, 37, 0.16)",
            overflow: "hidden",
            animation: `${rise} 280ms ease both`,
            ...motionSafe,
          },
        }}
      >
        {awb ? (
          <Box sx={{ height: 4, bgcolor: DHL_YELLOW }} />
        ) : (
          <Box
            aria-hidden
            sx={{
              position: "relative",
              height: 92,
              overflow: "hidden",
              background: `linear-gradient(135deg, ${alpha(DHL_YELLOW, 0.55)} 0%, ${alpha(ACCENT, 0.16)} 100%)`,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: "18%",
                top: 18,
                width: 52,
                height: 52,
                borderRadius: "14px",
                bgcolor: "#fff",
                border: "1.5px solid",
                borderColor: alpha(DHL_RED, 0.28),
                display: "grid",
                placeItems: "center",
                boxShadow: "0 8px 18px rgba(37, 37, 37, 0.12)",
                animation: `${glide} 2.4s ease-in-out infinite`,
                ...motionSafe,
              }}
            >
              <LocalShippingOutlinedIcon sx={{ fontSize: 26, color: ACCENT }} />
            </Box>
            <Box
              sx={{
                position: "absolute",
                right: "22%",
                top: 14,
                width: 56,
                height: 56,
                borderRadius: "16px",
                bgcolor: alpha(DHL_YELLOW, 0.45),
                border: "1.5px solid",
                borderColor: alpha(DHL_RED, 0.35),
                display: "grid",
                placeItems: "center",
                animation: `${bob} 2s ease-in-out infinite`,
                ...motionSafe,
              }}
            >
              <PlaceOutlinedIcon sx={{ fontSize: 30, color: DHL_RED }} />
            </Box>
            <Box
              sx={{
                position: "absolute",
                left: 12,
                bottom: 8,
                px: 0.8,
                py: 0.3,
                borderRadius: "8px",
                bgcolor: DHL_YELLOW,
                color: DHL_RED,
                fontFamily: FONT,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 0.4,
                animation: `${bob} 2.2s ease-in-out infinite`,
                ...motionSafe,
              }}
            >
              DHL
            </Box>
          </Box>
        )}
        <Box sx={{ px: "21px", pt: awb ? "18px" : "16px", pb: 1.25 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            {awb ? (
              <Box
                sx={{
                  bgcolor: DHL_YELLOW,
                  color: DHL_RED,
                  fontWeight: 800,
                  fontSize: 11,
                  letterSpacing: 0.6,
                  px: 0.75,
                  py: 0.35,
                  lineHeight: 1,
                  fontFamily: FONT,
                  borderRadius: "4px",
                  animation: `${bob} 2.4s ease-in-out infinite`,
                  ...motionSafe,
                }}
              >
                DHL
              </Box>
            ) : null}
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: 22,
                letterSpacing: -0.4,
                lineHeight: 1.15,
                color: INK,
              }}
            >
              {row?.Destino || t("courierControl.dhlTitle")}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mt: 1.1 }}
          >
            <ConfirmationNumberOutlinedIcon
              sx={{ fontSize: 16, color: MUTED }}
            />
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: 13,
                color: MUTED,
                letterSpacing: 0.2,
              }}
            >
              {awb
                ? t("courierControl.dhlAwb", { awb })
                : t("courierControl.dhlNoAwb")}
            </Typography>
          </Stack>

          {tracking?.description ? (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="flex-start"
              sx={{ mt: 0.75 }}
            >
              <Inventory2OutlinedIcon
                sx={{ fontSize: 16, color: ACCENT, mt: "2px" }}
              />
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: 14,
                  color: INK,
                  fontWeight: 600,
                  lineHeight: 1.35,
                }}
              >
                {tracking.description}
              </Typography>
            </Stack>
          ) : null}

          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1.35 }}
            flexWrap="wrap"
            useFlexGap
          >
            {latest ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  fontFamily: FONT,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  px: 1,
                  py: 0.4,
                  borderRadius: 999,
                  bgcolor:
                    latestKind === "delivered"
                      ? alpha("#2f6b4f", 0.12)
                      : alpha("#7f9dac", 0.18),
                  color: latestKind === "delivered" ? "#2f6b4f" : ACCENT,
                }}
              >
                {LatestIcon ? <LatestIcon sx={{ fontSize: 15 }} /> : null}
                {latest.description}
              </Box>
            ) : null}
            {showApiStatus ? (
              <Box
                sx={{
                  fontFamily: FONT,
                  fontSize: 12,
                  fontWeight: 700,
                  px: 1,
                  py: 0.4,
                  borderRadius: 999,
                  bgcolor: alpha("#7f9dac", 0.18),
                  color: ACCENT,
                }}
              >
                {apiStatus}
              </Box>
            ) : null}
            {tracking?.estimatedDeliveryDate ? (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontFamily: FONT,
                  fontSize: 12,
                  px: 1,
                  py: 0.4,
                  borderRadius: 999,
                  border: `1px solid ${HAIRLINE}`,
                  color: MUTED,
                }}
              >
                <ScheduleOutlinedIcon sx={{ fontSize: 14 }} />
                {t("courierControl.dhlEta", {
                  date: tracking.estimatedDeliveryDate,
                })}
              </Box>
            ) : null}
          </Stack>
        </Box>

        <Box sx={{ px: "21px", pb: "13px", maxHeight: 380, overflow: "auto" }}>
          {error ? (
            <Box
              sx={{
                mb: 1.5,
                px: 1.5,
                py: 1.1,
                borderRadius: "13px",
                bgcolor: "#f8efe8",
                color: "#6b3a2a",
                fontFamily: FONT,
                fontSize: 13.5,
                lineHeight: 1.45,
              }}
            >
              {error}
            </Box>
          ) : null}
          {busy === "track" ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ py: 2 }}
            >
              <CircularProgress size={16} sx={{ color: ACCENT }} />
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: MUTED }}>
                {t("courierControl.dhlTracking")}
              </Typography>
            </Stack>
          ) : null}
          {events.length ? (
            <Stack spacing={0.25} sx={{ mt: 0.5 }}>
              {events.map((event, index) => {
                const kind = eventKind(event);
                const Icon = EVENT_ICONS[kind];
                const current = index === 0;
                return (
                  <Stack
                    key={`${event.date}-${event.time}-${index}`}
                    direction="row"
                    spacing={1.25}
                    alignItems="stretch"
                  >
                    <Box
                      sx={{
                        width: 34,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                          bgcolor: current ? DHL_YELLOW : "#fff",
                          color: current ? DHL_RED : MUTED,
                          border: `1px solid ${current ? DHL_YELLOW : HAIRLINE}`,
                          boxShadow: current
                            ? "0 0 0 4px rgba(255, 204, 0, 0.28)"
                            : "none",
                          animation: current
                            ? `${pulseRing} 1.8s ease-out infinite`
                            : "none",
                          ...motionSafe,
                        }}
                      >
                        <Icon sx={{ fontSize: 16 }} />
                      </Box>
                      {index < events.length - 1 ? (
                        <Box
                          sx={{
                            width: "1px",
                            flex: 1,
                            minHeight: 12,
                            bgcolor: HAIRLINE,
                            my: 0.4,
                          }}
                        />
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        mb: 1,
                        px: 1.35,
                        py: 1,
                        borderRadius: "14px",
                        bgcolor: current ? "#fff" : "transparent",
                        border: current
                          ? `1px solid ${HAIRLINE}`
                          : "1px solid transparent",
                        animation: `${rise} 420ms ease both`,
                        animationDelay: `${Math.min(index, 8) * 45}ms`,
                        ...motionSafe,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: 14.5,
                          lineHeight: 1.3,
                          color: INK,
                          fontWeight: current ? 700 : 500,
                        }}
                      >
                        {event.description || event.code || "—"}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mt: 0.45 }}
                      >
                        <Stack
                          direction="row"
                          spacing={0.4}
                          alignItems="center"
                        >
                          <ScheduleOutlinedIcon
                            sx={{ fontSize: 13, color: MUTED }}
                          />
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: 11.5,
                              letterSpacing: 0.2,
                              color: MUTED,
                            }}
                          >
                            {formatWhen(event) || "—"}
                          </Typography>
                        </Stack>
                        {event.location ? (
                          <Stack
                            direction="row"
                            spacing={0.4}
                            alignItems="center"
                          >
                            <PlaceOutlinedIcon
                              sx={{ fontSize: 13, color: MUTED }}
                            />
                            <Typography
                              sx={{
                                fontFamily: FONT,
                                fontSize: 11.5,
                                letterSpacing: 0.2,
                                color: MUTED,
                              }}
                            >
                              {event.location}
                            </Typography>
                          </Stack>
                        ) : null}
                      </Stack>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          ) : awb && busy !== "track" && !error ? (
            <Typography
              sx={{ fontFamily: FONT, fontSize: 13.5, color: MUTED, py: 1 }}
            >
              {t("courierControl.dhlNoEvents")}
            </Typography>
          ) : null}
        </Box>

        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
          sx={{
            px: "21px",
            pb: "18px",
            pt: 0.75,
            borderTop: `1px solid ${HAIRLINE}`,
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              fontFamily: FONT,
              textTransform: "none",
              color: MUTED,
              fontWeight: 600,
            }}
          >
            {t("courierControl.cancel")}
          </Button>
          {canAskCancel ? (
            <Button
              onClick={() => setCancelOpen(true)}
              disabled={Boolean(busy)}
              sx={{
                fontFamily: FONT,
                textTransform: "none",
                color: DHL_RED,
                fontWeight: 700,
              }}
            >
              {t("courierControl.dhlCancel")}
            </Button>
          ) : null}
          {awb ? (
            <Tooltip
              title={canDownloadLabel ? "" : t("courierControl.dhlLabelLocked")}
            >
              <span>
                <Button
                  onClick={handleLabel}
                  disabled={Boolean(busy) || !canDownloadLabel}
                  variant="contained"
                  disableElevation
                  startIcon={
                    canDownloadLabel ? (
                      <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <LockOutlinedIcon sx={{ fontSize: 15 }} />
                    )
                  }
                  sx={{
                    fontFamily: FONT,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13,
                    minHeight: 34,
                    borderRadius: 999,
                    bgcolor: ACCENT,
                    px: 1.6,
                    py: 0.4,
                    "&.Mui-disabled": {
                      bgcolor: "#eceae6",
                      color: "#8a867c",
                    },
                    "&:hover": { bgcolor: "#3d5662" },
                  }}
                >
                  {busy === "label"
                    ? t("courierControl.docsGenerating")
                    : t("courierControl.dhlLabel")}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Tooltip
              title={canCreate ? "" : t("courierControl.dhlCreateLocked")}
            >
              <span>
                <Button
                  variant="contained"
                  disableElevation
                  disabled={Boolean(busy) || !canCreate}
                  startIcon={
                    canCreate ? (
                      <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <LockOutlinedIcon sx={{ fontSize: 15 }} />
                    )
                  }
                  onClick={() => {
                    if (canCreate) setConfirmOpen(true);
                  }}
                  sx={{
                    fontFamily: FONT,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13,
                    minHeight: 34,
                    borderRadius: 999,
                    bgcolor: ACCENT,
                    px: 1.6,
                    "&.Mui-disabled": {
                      bgcolor: "#eceae6",
                      color: "#8a867c",
                    },
                    "&:hover": { bgcolor: "#3d5662" },
                  }}
                >
                  {busy === "create"
                    ? t("courierControl.docsGenerating")
                    : t("courierControl.dhlCreate")}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Dialog>
      <Dialog
        open={confirmOpen}
        onClose={() => (busy === "create" ? null : setConfirmOpen(false))}
        maxWidth="xs"
        fullWidth
        transitionDuration={160}
        slotProps={dialogMotion}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            bgcolor: SURFACE,
            color: INK,
            fontFamily: FONT,
            border: `1px solid ${HAIRLINE}`,
            overflow: "hidden",
            animation: `${rise} 280ms ease both`,
            ...motionSafe,
          },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "relative",
            height: 92,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${alpha(DHL_YELLOW, 0.55)} 0%, ${alpha(ACCENT, 0.16)} 100%)`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: "16%",
              top: 18,
              width: 52,
              height: 52,
              borderRadius: "14px",
              bgcolor: "#fff",
              border: "1.5px solid",
              borderColor: alpha(DHL_RED, 0.28),
              display: "grid",
              placeItems: "center",
              boxShadow: "0 8px 18px rgba(37, 37, 37, 0.12)",
              animation: `${glide} 2.4s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            <LocalShippingOutlinedIcon sx={{ fontSize: 26, color: ACCENT }} />
          </Box>
          <Box
            sx={{
              position: "absolute",
              right: "16%",
              top: 14,
              width: 56,
              height: 56,
              borderRadius: "16px",
              bgcolor: alpha(DHL_YELLOW, 0.45),
              border: "1.5px solid",
              borderColor: alpha(DHL_RED, 0.35),
              display: "grid",
              placeItems: "center",
              animation: `${bob} 2s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            <PlaceOutlinedIcon sx={{ fontSize: 30, color: DHL_RED }} />
          </Box>
          <Box
            sx={{
              position: "absolute",
              left: 12,
              bottom: 8,
              px: 0.8,
              py: 0.3,
              borderRadius: "8px",
              bgcolor: DHL_YELLOW,
              color: DHL_RED,
              fontFamily: FONT,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 0.4,
              animation: `${bob} 2.2s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            DHL
          </Box>
        </Box>
        <Box sx={{ px: "21px", pt: "16px", pb: "8px" }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: -0.3,
            }}
          >
            {t("courierControl.dhlCreateTitle")}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontFamily: FONT,
              fontSize: 14,
              lineHeight: 1.45,
              color: INK,
            }}
          >
            {row?.Destino || "—"}
          </Typography>
          {row?.DireccionEnvio ? (
            <Typography
              sx={{
                mt: 0.4,
                fontFamily: FONT,
                fontSize: 13,
                color: MUTED,
                lineHeight: 1.4,
              }}
            >
              {row.DireccionEnvio}
            </Typography>
          ) : null}
          {row?.contenido ? (
            <Typography
              sx={{
                mt: 1,
                fontFamily: FONT,
                fontSize: 13.5,
                fontWeight: 600,
              }}
            >
              {row.contenido}
            </Typography>
          ) : null}
          <Box
            sx={{
              mt: 1.5,
              px: 1.5,
              py: 1.1,
              borderRadius: "13px",
              bgcolor: "#fff6d8",
              color: INK,
              fontFamily: FONT,
              fontSize: 13.5,
              lineHeight: 1.45,
            }}
          >
            {t("courierControl.dhlCreateWarn")}
          </Box>
        </Box>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ px: "21px", pb: "18px" }}
        >
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={busy === "create"}
            sx={{
              fontFamily: FONT,
              textTransform: "none",
              color: MUTED,
              fontWeight: 600,
            }}
          >
            {t("courierControl.cancel")}
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy === "create" || !canCreate}
            onClick={handleCreate}
            sx={{
              fontFamily: FONT,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "13px",
              bgcolor: DHL_RED,
              "&:hover": { bgcolor: "#b0040e" },
            }}
          >
            {busy === "create"
              ? t("courierControl.docsGenerating")
              : t("courierControl.dhlCreateConfirm")}
          </Button>
        </Stack>
      </Dialog>
      <Dialog
        open={cancelOpen}
        onClose={() => (busy === "cancel" ? null : setCancelOpen(false))}
        maxWidth="xs"
        fullWidth
        transitionDuration={160}
        slotProps={dialogMotion}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            bgcolor: SURFACE,
            color: INK,
            fontFamily: FONT,
            border: `1px solid ${HAIRLINE}`,
            overflow: "hidden",
            animation: `${rise} 280ms ease both`,
            ...motionSafe,
          },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "relative",
            height: 92,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${alpha(DHL_RED, 0.14)} 0%, ${alpha(DHL_YELLOW, 0.28)} 100%)`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: "18%",
              top: 18,
              width: 52,
              height: 52,
              borderRadius: "14px",
              bgcolor: "#fff",
              border: "1.5px solid",
              borderColor: alpha(DHL_RED, 0.28),
              display: "grid",
              placeItems: "center",
              boxShadow: "0 8px 18px rgba(212, 5, 17, 0.16)",
              animation: `${slideAway} 2.6s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            <LocalShippingOutlinedIcon sx={{ fontSize: 26, color: ACCENT }} />
          </Box>
          <Box
            sx={{
              position: "absolute",
              right: "18%",
              top: 16,
              width: 56,
              height: 56,
              borderRadius: "16px",
              bgcolor: alpha(DHL_RED, 0.12),
              border: "1.5px solid",
              borderColor: alpha(DHL_RED, 0.45),
              display: "grid",
              placeItems: "center",
              animation: `${shakeMark} 2.2s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            <HighlightOffOutlinedIcon sx={{ fontSize: 30, color: DHL_RED }} />
          </Box>
          <Box
            sx={{
              position: "absolute",
              left: 12,
              bottom: 8,
              px: 0.8,
              py: 0.3,
              borderRadius: "8px",
              bgcolor: DHL_YELLOW,
              color: DHL_RED,
              fontFamily: FONT,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 0.4,
              animation: `${bob} 2s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            DHL
          </Box>
        </Box>
        <Box sx={{ px: "21px", pt: "16px", pb: "8px" }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: -0.3,
            }}
          >
            {t("courierControl.dhlCancelTitle")}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontFamily: FONT,
              fontSize: 13.5,
              lineHeight: 1.45,
              color: MUTED,
            }}
          >
            {t("courierControl.dhlCancelWarn", { awb })}
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder={t("courierControl.dhlCancelReasonDefault")}
            sx={{
              mt: 1.5,
              "& .MuiInputBase-root": {
                fontFamily: FONT,
                borderRadius: "12px",
                bgcolor: "#fff",
              },
            }}
          />
        </Box>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ px: "21px", pb: "18px" }}
        >
          <Button
            onClick={() => setCancelOpen(false)}
            disabled={busy === "cancel"}
            sx={{
              fontFamily: FONT,
              textTransform: "none",
              color: MUTED,
              fontWeight: 600,
            }}
          >
            {t("courierControl.cancel")}
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy === "cancel"}
            onClick={handleCancelShipment}
            sx={{
              fontFamily: FONT,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "13px",
              bgcolor: DHL_RED,
              "&:hover": { bgcolor: "#b0040e" },
            }}
          >
            {busy === "cancel"
              ? t("courierControl.docsGenerating")
              : t("courierControl.dhlCancelConfirm")}
          </Button>
        </Stack>
      </Dialog>
    </>
  );
}
